import { cn } from '@app/utils/cn';
import { isDefined } from '@app/utils/is';
import { isEmpty } from 'ramda';
import React from 'react';

import { RadioDot } from '../RadioDot';

interface Props extends React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
  description?: React.ReactNode;
  postfix?: React.ReactNode;
}

export const RadioPill = ({ children, description, postfix, color = 'white', ...props }: React.PropsWithChildren<Props>) => {
  const radioId = React.useId();
  const descriptionId = React.useId();
  const postfixId = React.useId();

  const hasDescription = isDefined(description) && !isEmpty(description);
  const hasPostfix = isDefined(postfix) && !isEmpty(postfix);

  const describedByIds = [...(hasDescription ? [descriptionId] : []), ...(hasPostfix ? [postfixId] : [])];
  const ariaDescribedBy = isEmpty(describedByIds) ? undefined : describedByIds.join(' ');

  return (
    <div
      className={cn(
        'relative flex items-center justify-between gap-5 rounded-regular border border-transparent px-5 py-3 transition-colors has-[:checked]:border-neutral-20 has-[:focus-visible]:focus-outline has-[:checked]:hover:border-neutral-40',
        {
          'bg-neutral-96': color === 'gray',
          'bg-white': color === 'white',
        },
      )}
    >
      <div className="flex gap-5">
        <RadioDot {...props} className="mt-0.5" hasFocusRing={false} id={radioId} />
        <div className="flex flex-col">
          <label htmlFor={radioId} aria-describedby={ariaDescribedBy} className="body-1 absolute-cursor-pointer">
            <span>{children}</span>
          </label>
          {hasDescription && (
            <div id={descriptionId} className="text-neutral-40 body-5">
              {description}
            </div>
          )}
        </div>
      </div>
      {hasPostfix && (
        <div id={postfixId} className="flex gap-1 text-neutral-40 body-5">
          {postfix}
        </div>
      )}
    </div>
  );
};
